import {
  IMAGE_MAX_OUTPUT_COUNT,
  IMAGE_PARAMETER_SCHEMA_GEMINI,
  IMAGE_PARAMETER_SCHEMA_OPENAI,
  ImageGenerationContractError,
  normalizeImageParameterSchema,
  normalizeImageResolution,
  normalizeImageSelection,
  resolveImageGenerationFormOptions
} from './capabilities.js'

function text(value) {
  return typeof value === 'string' ? value.trim() : ''
}

function deepFreeze(value) {
  if (!value || typeof value !== 'object' || Object.isFrozen(value)) return value
  Object.values(value).forEach(deepFreeze)
  return Object.freeze(value)
}

/**
 * Serialize the normalized UI selection into the provider-neutral creator
 * contract. OpenAI-only quality is intentionally never emitted for Gemini.
 */
export function serializeImageTechnicalParams({
  schema,
  selection = {},
  technicalOverrides = {}
} = {}) {
  const normalizedSchema = normalizeImageParameterSchema(schema) || IMAGE_PARAMETER_SCHEMA_GEMINI
  const params = normalizedSchema === IMAGE_PARAMETER_SCHEMA_OPENAI
    ? {
        parameter_schema: IMAGE_PARAMETER_SCHEMA_OPENAI,
        size: text(selection.size || selection.aspectRatio) || '1:1',
        resolution: (normalizeImageResolution(
          selection.resolution ?? selection.resolutionTier ?? selection.resolution_tier ?? selection.imageSize
        ) || '1K').toLowerCase(),
        quality: text(selection.quality).toLowerCase() || 'auto',
        background: text(selection.background).toLowerCase() || 'auto',
        output_format: text(selection.outputFormat).toLowerCase() || 'png',
        n: 1,
        moderation: text(selection.moderation).toLowerCase() || 'auto',
        style: text(selection.style) || 'none'
      }
    : {
        parameter_schema: normalizedSchema || IMAGE_PARAMETER_SCHEMA_GEMINI,
        aspect_ratio: text(selection.aspectRatio) || '1:1',
        image_size: text(selection.imageSize).toUpperCase() || '1K',
        style: text(selection.style) || 'none'
      }

  ;['edge_enhance', 'auto_crop', 'print_crop'].forEach((key) => {
    if (typeof technicalOverrides?.[key] === 'boolean') params[key] = technicalOverrides[key]
  })
  if (text(technicalOverrides?.output_format)) {
    params.output_format = text(technicalOverrides.output_format).toLowerCase()
  }
  return deepFreeze(params)
}

export function inferAndSerializeImageTechnicalParams(input = {}) {
  const schema = normalizeImageParameterSchema(input.schema) ||
    normalizeImageParameterSchema(
      input.family?.parameterSchema ?? input.family?.parameter_schema
    ) || IMAGE_PARAMETER_SCHEMA_GEMINI
  return serializeImageTechnicalParams({ ...input, schema })
}

export function compileImageTechnicalParams({
  family,
  rights = {},
  selection = {},
  technicalOverrides = {},
  maxOutputCount = IMAGE_MAX_OUTPUT_COUNT
} = {}) {
  if (!family?.available || !text(family?.selectedModelKey ?? family?.selected_model_key)) {
    throw new ImageGenerationContractError('image_model_unavailable', '当前图片模型不可用', {
      path: 'family'
    })
  }
  const requested = normalizeImageSelection(selection, {}, { maxOutputCount })
  const resolved = resolveImageGenerationFormOptions(family, rights, requested, { maxOutputCount })
  if (![IMAGE_PARAMETER_SCHEMA_GEMINI, IMAGE_PARAMETER_SCHEMA_OPENAI].includes(resolved.schema)) {
    throw new ImageGenerationContractError(
      'image_parameter_schema_unavailable',
      '当前图片模型没有可用参数契约',
      { path: 'family.parameter_schema' }
    )
  }
  const fields = resolved.schema === IMAGE_PARAMETER_SCHEMA_OPENAI
    ? [
        'size',
        'resolution',
        'quality',
        'background',
        'outputFormat',
        'moderation',
        'outputCount',
        'style'
      ]
    : ['aspectRatio', 'imageSize', 'outputCount', 'style']
  const mismatch = fields.find((field) => String(requested[field]) !== String(resolved.selection[field]))
  if (mismatch) {
    throw new ImageGenerationContractError(
      'unsupported_image_option',
      '当前模型或会员能力不支持所选图片参数',
      { path: `selection.${mismatch}` }
    )
  }
  if (resolved.schema === IMAGE_PARAMETER_SCHEMA_OPENAI && (
    !resolved.selection.size || !resolved.selection.resolution || !resolved.selection.quality
  )) {
    throw new ImageGenerationContractError(
      'image_specification_unavailable',
      '当前模型没有可用图片尺寸',
      { path: !resolved.selection.size
        ? 'selection.size'
        : (!resolved.selection.resolution ? 'selection.resolution' : 'selection.quality') }
    )
  }
  if (resolved.schema !== IMAGE_PARAMETER_SCHEMA_OPENAI && (
    !resolved.selection.aspectRatio || !resolved.selection.imageSize
  )) {
    throw new ImageGenerationContractError(
      'image_specification_unavailable',
      '当前模型没有可用图片规格',
      { path: 'selection' }
    )
  }
  return deepFreeze({
    requested,
    resolved,
    technicalParams: serializeImageTechnicalParams({
      schema: resolved.schema,
      selection: resolved.selection,
      technicalOverrides
    })
  })
}
